import { useState } from 'react';
import type { Difficulty } from '../../ai';
import { makeConfig, type PresetId } from '../../engine';
import { t, type MessageKey } from '../../i18n';
import type { MatchSetup } from '../../app/matchHost';
import { Choice, Field } from '../components/Form';

type Opponent = 'ai' | 'hotseat';
type SidePref = 'random' | 'S' | 'N';
type Preset = Exclude<PresetId, 'CUSTOM'>;

export const AI_NAMES: Record<Difficulty, string> = {
  easy: 'The Apprentice',
  standard: 'The Clerk',
  hard: 'The Appraiser',
};

export function randomSeed(): string {
  const n = Math.floor(Math.random() * 36 ** 6);
  return `duel-${Date.now().toString(36)}-${n.toString(36)}`;
}

export function DuelSetupScreen({
  hasSave,
  onStart,
  onBack,
}: {
  hasSave: boolean;
  onStart: (s: MatchSetup) => void;
  onBack: () => void;
}) {
  const [opponent, setOpponent] = useState<Opponent>('ai');
  const [difficulty, setDifficulty] = useState<Difficulty>('standard');
  const [side, setSide] = useState<SidePref>('random');
  const [preset, setPreset] = useState<Preset>('A');
  const [you, setYou] = useState('You');
  const [south, setSouth] = useState('Player 1');
  const [north, setNorth] = useState('Player 2');
  const [seed, setSeed] = useState('');

  const start = () => {
    const finalSeed = seed.trim() || randomSeed();
    if (opponent === 'ai') {
      const players: [string, string] = side === 'N' ? ['ai', 'you'] : ['you', 'ai'];
      onStart({
        config: makeConfig({
          preset,
          seating: { players, assignment: side === 'random' ? 'random' : 'fixed' },
        }),
        seed: finalSeed,
        controllers: {
          you: { kind: 'human', name: you.trim() || 'You' },
          ai: { kind: 'ai', name: AI_NAMES[difficulty], difficulty },
        },
      });
    } else {
      onStart({
        config: makeConfig({
          preset,
          seating: { players: ['south', 'north'], assignment: 'fixed' },
        }),
        seed: finalSeed,
        controllers: {
          south: { kind: 'human', name: south.trim() || t('side.S') },
          north: { kind: 'human', name: north.trim() || t('side.N') },
        },
      });
    }
  };

  const presetOptions = (['A', 'B', 'BASELINE'] as const).map((p) => ({
    value: p,
    label: t(`setup.preset.${p}` as MessageKey),
    hint: t(`setup.preset.${p}.hint` as MessageKey),
  }));

  return (
    <main className="page">
      <header className="page-head">
        <button type="button" className="btn btn-small" onClick={onBack}>
          ← {t('menu.back')}
        </button>
        <h1>{t('setup.title')}</h1>
      </header>
      <form
        className="setup-form"
        onSubmit={(e) => {
          e.preventDefault();
          start();
        }}
      >
        <Choice
          legend={t('setup.opponent')}
          value={opponent}
          onChange={setOpponent}
          options={[
            { value: 'ai', label: t('setup.opponent.ai') },
            { value: 'hotseat', label: t('setup.opponent.hotseat') },
          ]}
        />
        {opponent === 'ai' ? (
          <>
            <Choice
              legend={t('setup.difficulty')}
              value={difficulty}
              onChange={setDifficulty}
              options={(['easy', 'standard', 'hard'] as const).map((d) => ({
                value: d,
                label: `${t(`setup.difficulty.${d}` as MessageKey)} · ${AI_NAMES[d]}`,
                hint: t(`setup.difficulty.${d}.hint` as MessageKey),
              }))}
            />
            <Choice
              legend={t('setup.side')}
              value={side}
              onChange={setSide}
              options={[
                { value: 'random', label: t('setup.side.random') },
                { value: 'S', label: t('side.S') },
                { value: 'N', label: t('side.N') },
              ]}
            />
            <Field label={t('setup.names')}>
              <input
                type="text"
                value={you}
                maxLength={24}
                onChange={(e) => setYou(e.target.value)}
              />
            </Field>
          </>
        ) : (
          <div className="field-row">
            <Field label={t('setup.nameSouth')}>
              <input
                type="text"
                value={south}
                maxLength={24}
                onChange={(e) => setSouth(e.target.value)}
              />
            </Field>
            <Field label={t('setup.nameNorth')}>
              <input
                type="text"
                value={north}
                maxLength={24}
                onChange={(e) => setNorth(e.target.value)}
              />
            </Field>
          </div>
        )}
        <Choice
          legend={t('setup.preset')}
          value={preset}
          onChange={setPreset}
          options={presetOptions}
        />
        <Field label={t('setup.seed')} hint={t('setup.seedHint')}>
          <input
            type="text"
            value={seed}
            maxLength={64}
            placeholder="—"
            onChange={(e) => setSeed(e.target.value)}
          />
        </Field>
        {hasSave && <p className="dim">{t('setup.replaceSave')}</p>}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary btn-large">
            {t('setup.start')}
          </button>
        </div>
      </form>
    </main>
  );
}
